import { animate, state, style, transition, trigger } from '@angular/animations';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { CustomerPortal } from '../../models/customer-portal';
import { CustomerPortalSubmenu } from '../../models/customer-portal-submenu';
import { NavigationEnd, Router } from '@angular/router';
import { DataService } from 'src/app/core/services/data.service';
import { Subscription } from 'rxjs';
import { filter } from 'rxjs/operators';
import { toAbsolutePortalNavigationUrl } from '../../utils/customer-portals-submenus.util';

@Component({
  selector: 'app-sidenav',
  templateUrl: './sidenav.component.html',
  styleUrls: ['./sidenav.component.scss'],
  standalone: false,
  animations: [
    trigger('expandCollapse', [
      state(
        'collapsed',
        style({
          height: '0px',
          opacity: 0,
          overflow: 'hidden',
        }),
      ),
      state(
        'expanded',
        style({
          height: '*',
          opacity: 1,
          overflow: 'hidden',
        }),
      ),
      transition('expanded <=> collapsed', animate('200ms ease-in-out')),
    ]),
  ],
})
export class SidenavComponent implements OnInit, OnDestroy {
  _customerApps: CustomerPortal[] = [];
  private readonly baseSubmenuPadding = 40;
  private readonly nestedSubmenuIncrement = 5;
  private expandedSubmenuIds = new Set<number>();
  private expandedPortalIds = new Set<string>();
  private routerSubscription?: Subscription;

  @Input() set customerApps(value: CustomerPortal[]) {
    this._customerApps = value;
    this.expandActiveSubmenus();
  }
  @Output() onCustomerPortalClicked = new EventEmitter();
  @Output() onCustomerPortalKeydown = new EventEmitter();

  private compactSidenav: boolean = false;

  get customerApps(): CustomerPortal[] {
    return this._customerApps;
  }

  get showSidenavControls(): boolean {
    return !this.compactSidenav;
  }

  constructor(
    private router: Router,
    private dataService: DataService,
  ) {}

  ngOnInit(): void {
    this.expandActiveSubmenus();
    this.routerSubscription = this.router.events
      .pipe(filter((event): event is NavigationEnd => event instanceof NavigationEnd))
      .subscribe(() => this.expandActiveSubmenus());
  }

  ngOnDestroy(): void {
    this.routerSubscription?.unsubscribe();
  }

  isPortalActive(app: CustomerPortal) {
    return this.hasPortalSubmenus(app)
      ? false
      : this.router.url.includes(app.baseUrl) || this.router.url.endsWith(app.name.toLowerCase());
  }

  isSubmenuActive(submenu: CustomerPortalSubmenu) {
    // Container submenus come from the API with `uri: ""`. `String.includes('')`
    // is always true, which would falsely auto-expand the container on every page.
    if (!submenu.uri) return false;
    return this.router.url.includes(submenu.uri);
  }

  onCustomerPortalClickedEmit(value: { app: CustomerPortal }) {
    if (this.hasPortalSubmenus(value.app)) {
      const event = new MouseEvent('click');
      this.onTogglePortalClick(event as MouseEvent, value.app);
      if (this.compactSidenav) {
        this.compressSidenav();
      }
    } else {
      this.onCustomerPortalClicked.emit(value);
    }
  }

  onCustomerPortalKeydownEmit(value: { $event: any; app: CustomerPortal }) {
    this.onCustomerPortalKeydown.emit(value);
  }

  onSubmenuClicked(submenu: CustomerPortalSubmenu) {
    if (this.hasSubmenus(submenu)) {
      this.toggleSubmenuExpansion(submenu);
    } else if (submenu.uri) {
      this.router.navigateByUrl(toAbsolutePortalNavigationUrl(submenu.uri));
    }
  }

  getSubmenuPadding(level: number): number {
    return this.baseSubmenuPadding + this.nestedSubmenuIncrement * level;
  }

  hasPortalSubmenus(app: CustomerPortal): boolean {
    return Array.isArray(app.submenus) && app.submenus.length > 0;
  }

  hasSubmenus(submenu: CustomerPortalSubmenu): boolean {
    return Array.isArray(submenu.submenus) && submenu.submenus.length > 0;
  }

  isSubmenuExpanded(submenu: CustomerPortalSubmenu): boolean {
    return this.hasSubmenus(submenu) && this.expandedSubmenuIds.has(submenu.id);
  }

  isPortalExpanded(app: CustomerPortal): boolean {
    return this.hasPortalSubmenus(app) && this.expandedPortalIds.has(this.getPortalKey(app));
  }

  onPortalKeydown(event: KeyboardEvent, app: CustomerPortal) {
    if (event.key === 'ArrowRight' && this.hasPortalSubmenus(app) && !this.isPortalExpanded(app)) {
      event.preventDefault();
      this.togglePortalExpansion(app);
      return;
    }

    if (event.key === 'ArrowLeft' && this.hasPortalSubmenus(app) && this.isPortalExpanded(app)) {
      event.preventDefault();
      this.togglePortalExpansion(app);
      return;
    }

    if (this.hasPortalSubmenus(app) && (event.key === 'Enter' || event.key === ' ')) {
      event.preventDefault();
      this.togglePortalExpansion(app);
    } else {
      this.onCustomerPortalKeydown.emit({ $event: event, app });
    }
  }

  onSubmenuKeydown(event: KeyboardEvent, submenu: CustomerPortalSubmenu) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.onSubmenuClicked(submenu);
      return;
    }

    if (event.key === 'ArrowRight' && this.hasSubmenus(submenu) && !this.isSubmenuExpanded(submenu)) {
      event.preventDefault();
      this.toggleSubmenuExpansion(submenu);
      return;
    }

    if (event.key === 'ArrowLeft' && this.hasSubmenus(submenu) && this.isSubmenuExpanded(submenu)) {
      event.preventDefault();
      this.toggleSubmenuExpansion(submenu);
    }
  }

  onTogglePortalClick(event: MouseEvent | null, app: CustomerPortal) {
    if (event) {
      event.preventDefault();
      event.stopPropagation();
    }
    this.togglePortalExpansion(app);
  }

  onTogglePortalKeydown(event: KeyboardEvent, app: CustomerPortal) {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      this.togglePortalExpansion(app);
    }
  }

  getToggleAriaLabel(submenu: CustomerPortalSubmenu): string {
    const action = this.isSubmenuExpanded(submenu) ? 'Collapse' : 'Expand';
    return `${action} submenu ${submenu.name}`;
  }

  getPortalToggleAriaLabel(app: CustomerPortal): string {
    const action = this.isPortalExpanded(app) ? 'Collapse' : 'Expand';
    return `${action} menu ${app.name}`;
  }

  private toggleSubmenuExpansion(submenu: CustomerPortalSubmenu) {
    if (!this.hasSubmenus(submenu)) {
      return;
    }

    if (this.isSubmenuExpanded(submenu)) {
      this.expandedSubmenuIds.delete(submenu.id);
    } else {
      this.expandedSubmenuIds.add(submenu.id);
    }
    this.expandedSubmenuIds = new Set(this.expandedSubmenuIds);
  }

  private togglePortalExpansion(app: CustomerPortal) {
    if (!this.hasPortalSubmenus(app)) {
      return;
    }

    const key = this.getPortalKey(app);

    if (this.expandedPortalIds.has(key)) {
      this.expandedPortalIds.delete(key);
    } else {
      this.expandedPortalIds.add(key);
    }
    this.expandedPortalIds = new Set(this.expandedPortalIds);
  }

  private expandActiveSubmenus() {
    if (!this.customerApps?.length) {
      return;
    }

    const expandedIds = new Set(this.expandedSubmenuIds);
    const expandedPortalIds = new Set(this.expandedPortalIds);

    const markActive = (submenu: CustomerPortalSubmenu): boolean => {
      const hasChildren = this.hasSubmenus(submenu);
      const isActive = this.isSubmenuActive(submenu);
      let childActive = false;

      if (hasChildren) {
        childActive = submenu.submenus!.some((child) => markActive(child));
        if (childActive || isActive) {
          expandedIds.add(submenu.id);
        }
      }

      return isActive || childActive;
    };

    this.customerApps.forEach((app) => {
      const portalHasActiveSubmenus = (app.submenus ?? []).some((submenu) => markActive(submenu));

      if (portalHasActiveSubmenus || this.isPortalActive(app)) {
        expandedPortalIds.add(this.getPortalKey(app));
      }
    });

    this.expandedSubmenuIds = expandedIds;
    this.expandedPortalIds = expandedPortalIds;
  }

  compressSidenav(newVal?: boolean) {
    this.compactSidenav = newVal !== undefined ? newVal : !this.compactSidenav;
    this.dataService.setExpandedSidenavClosedState(this.compactSidenav);
  }

  private getPortalKey(app: CustomerPortal): string {
    return String(app.portalId ?? app.baseUrl ?? app.addonName);
  }
}
